import React, { useMemo, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/hooks";
import { Button } from "../Base/Button";
import { useUserDataFlattened } from "../../modules";
import cdnAsset_BbVwPaWC from "@/assets/cdn/svg/BbVwPaWC.svg";
import cdnAsset_BlyoKvvW from "@/assets/cdn/svg/BlyoKvvW.svg";
import cdnAsset_WSnlNayI from "@/assets/cdn/svg/WSnlNayI.svg";
import {
  useEnableDownloadAppFloatingOnHomeTop,
  useLoginRegisterConfig,
  usePlatformLogo,
} from "../../modules/homeDisplay/hooks";
import { useHomeDisplayStore } from "../../modules/homeDisplay/stores";
import { useCurrencySymbol } from "../../hooks/useUtils";
import { useNavigation, ROUTES } from "../../utils/navigation";
import { getCountryIdFromStorage } from "../../utils/countryCookie";
import { triggerPopup } from "../../utils/popupTriggers";
import { POPUP_CONFIGS } from "../../config/popupConfig";
import { openLoginRegisterPopup } from "../LoginRegisterPopup";
import { openDepositPopup } from "../DepositPopup";
import { resolvePageConfig, usePageConfig } from "../../hooks/usePageConfig";
import "./Header.scss";
import type { HeaderProps } from "./Header.types";
import type { RedirectConfigItem } from "../../modules/homeDisplay/types";
import CustomerIconImg from "@/assets/homePage/reskin/customer.webp";
import GameSearchIconImg from "@/assets/homePage/reskin/search.webp";

import InsuranceIconImg from "@/assets/homePage/reskin/insurance.webp";
import CountryRingImg from "@/assets/header/country-ring.webp";
import DefaultAvatarImg from "@/assets/profile/VIP0.webp";
import { Icon } from "../Icon";
import { utils } from "../..";
import { Badge } from "../Base/Badge";
import { useMessageNotification } from "@/modules/notification/useMessageNotification";
import { isDownloadAppBarSessionClosed } from "../../utils/downloadAppBarSession";

const FLAG_ICONS_CDN =
  "https://cdn.jsdelivr.net/npm/flag-icons@7.5.0/flags/1x1";

export const Header: React.FC<HeaderProps> = ({
  title,
  className = "",
  variant = "home",
  onNotificationClick,
  onBackClick,
}) => {
  const { t } = useI18n();
  const { navigate, goBack } = useNavigation();
  const location = useLocation();
  const pageConfigMap = usePageConfig();
  const currentPageConfig = useMemo(
    () => resolvePageConfig(location.pathname, pageConfigMap),
    [location.pathname, pageConfigMap]
  );
  const { isAuthenticated, data } = useUserDataFlattened();
  const balance = data?.balanceDetail?.balance ?? data?.balance;
  const { messageUnreadCount } = useMessageNotification();
  const enableFloatingOnHomeTop = useEnableDownloadAppFloatingOnHomeTop();
  const currencySymbol = useCurrencySymbol();
  const [balanceVisible, setBalanceVisible] = useState(true);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const sync = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--header-height", `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const platformLogo = usePlatformLogo();
  const logoSrc = utils.getImageUrl(platformLogo?.logoUrl || "");
  const loginRegisterConfig = useLoginRegisterConfig();
  const homeRegisterPromoText =
    loginRegisterConfig?.homeRegisterBtnFirstChargeText?.trim() ?? "";
  const redirectConfigs = useHomeDisplayStore(
    state => state.data?.redirectConfigs ?? []
  );
  const hasCountryList =
    Array.isArray(redirectConfigs) && redirectConfigs.length > 0;

  const { currentCountryDisplayName, currentCountryFlagCode } = useMemo(() => {
    const countryId =
      (utils.getObject("countryId") as string) ||
      getCountryIdFromStorage() ||
      (window._config?.countryId ?? "");
    const item = redirectConfigs.find(
      (c: RedirectConfigItem) =>
        (c.countryRedirectId ?? "").trim().toUpperCase() ===
        countryId.trim().toUpperCase()
    );
    const name =
      item?.countryName ?? item?.displayName ?? item?.name ?? item?.id ?? "";
    const raw = (item?.countryRedirectId ?? countryId ?? "").toString().trim();
    const flagCode = raw.length >= 2 ? raw.slice(0, 2).toLowerCase() : null;
    if (name) {
      return {
        currentCountryDisplayName: name,
        currentCountryFlagCode: flagCode,
      };
    }
    if (countryId.trim()) {
      const fromConfig = window._config?.countryList?.find(
        (c: { id?: string }) =>
          (c.id ?? "").trim().toUpperCase() === countryId.trim().toUpperCase()
      );
      const displayName =
        fromConfig?.name ?? fromConfig?.displayName ?? countryId.trim();
      const code = (countryId ?? "").slice(0, 2).toLowerCase();
      return {
        currentCountryDisplayName: displayName,
        currentCountryFlagCode: code || null,
      };
    }
    return { currentCountryDisplayName: "", currentCountryFlagCode: null };
  }, [redirectConfigs]);

  // DownloadAppBar 暂时隐藏，首页 Header 不再预留顶部偏移
  const [downloadAppBarVisible, setDownloadAppBarVisible] = useState(() => {
    if (utils.isApp() || utils.updateCheckIsPwa() || !enableFloatingOnHomeTop)
      return false;
    return !isDownloadAppBarSessionClosed();
  });
  useEffect(() => {
    const handler = (e: Event) =>
      setDownloadAppBarVisible((e as CustomEvent).detail);
    window.addEventListener("downloadAppBarChange", handler);
    return () => window.removeEventListener("downloadAppBarChange", handler);
  }, []);
  // 检查用户是否已注册过
  const hasRegistered = utils.getObject("has_registered");
  const handleLoginClick = () => {
    console.log("[Header] handleLoginClick clicked");
    openLoginRegisterPopup({ defaultMode: "login" });
  };

  const handleAvatarClick = () => {
    navigate(ROUTES.PROFILE, { state: { fromBottomNav: true } });
  };

  const handleBalanceClick = () => {
    navigate(ROUTES.ACCOUNT_DETAILS);
  };

  const handleDepositClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openDepositPopup();
  };

  const handleMessageClick = () => {
    navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=2`);
    if (onNotificationClick) {
      onNotificationClick();
    }
  };

  const handleServiceClick = () => {
    // 优先尝试打开 Tawk.to 聊天窗口（右下角嵌入式客服）
    if (
      (window as any).Tawk_API &&
      typeof (window as any).Tawk_API.maximize === "function"
    ) {
      (window as any).Tawk_API.maximize();
      return;
    }
    // 其次尝试打开 LiveChat
    if ((window as any).LiveChatWidget) {
      (window as any).LiveChatWidget.call("maximize");
      return;
    }
    // 降级：跳转到消息中心客服标签
    navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=1`);
  };

  const handleCountryClick = () => {
    triggerPopup(POPUP_CONFIGS.COUNTRY_LIST.id);
  };

  // 处理回退按钮点击
  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      goBack();
    }
  };

  // 标题 / 右侧 icon 统一走 usePageConfig 对应路由配置
  const pageTitle = currentPageConfig?.title || title || t("navigation.page");
  const rightIcon = currentPageConfig?.icon;

  // 子页面 Header 渲染
  if (variant === "subpage") {
    return (
      <header ref={headerRef} className={`subpage-header ${className}`}>
        <div className="subpage-header__content">
          <button
            className="subpage-header__back-btn"
            onClick={handleBackClick}
            aria-label="Go back"
          >
            <img
              src={cdnAsset_BbVwPaWC}
              alt=""
              className="subpage-header__back-icon"
              draggable={false}
            />
          </button>
          <span className="subpage-header__title">{pageTitle}</span>
          {rightIcon ? (
            <div className="subpage-header__right-icon">{rightIcon}</div>
          ) : null}
        </div>
      </header>
    );
  }

  // 首页 Header 渲染
  return (
    <header
      ref={headerRef}
      className={`mobile-header ${isAuthenticated ? "mobile-header--authenticated" : ""} ${className}`}
      style={{
        marginTop: downloadAppBarVisible ? "3rem" : "0",
      }}
    >
      <div className="mobile-header__content">
        {isAuthenticated ? (
          /* 已登录：左侧 Logo，右侧用户余额和消息 */
          <div className="mobile-header__auth-bar">
            {/* Logo 靠左 */}
            <div className="mobile-header__logo">
              {logoSrc && (
                <img
                  src={logoSrc}
                  alt="Logo"
                  className="mobile-header__logo-img"
                  draggable={false}
                />
              )}
            </div>
            {/* 右侧用户信息区域 */}
            <div className="mobile-header__actions mobile-header__actions--logged-in">
              {/* 余额和充值按钮 */}
              <div
                className="mobile-header__user-balance-box"
                onClick={handleBalanceClick}
              >
                {/* 国旗 */}
                {currentCountryFlagCode && (
                  <img
                    src={`${FLAG_ICONS_CDN}/${currentCountryFlagCode}.svg`}
                    alt=""
                    className="mobile-header__user-flag"
                    draggable={false}
                  />
                )}
                {/* 余额 */}
                <span className="mobile-header__user-balance-text">
                  {currencySymbol}
                  {typeof balance === "number" && balance != null
                    ? balance.toString().split(".")[1]?.length > 2
                      ? balance.toFixed(2)
                      : balance
                    : "0"}
                </span>
                {/* 充值按钮 */}
                <button
                  className="mobile-header__user-deposit-btn"
                  onClick={handleDepositClick}
                  aria-label="Deposit"
                >
                  <img
                    src={cdnAsset_WSnlNayI}
                    alt=""
                    className="mobile-header__user-deposit-icon"
                    draggable={false}
                  />
                </button>
              </div>
              {/* 消息按钮 */}
              <Badge
                content={
                  messageUnreadCount > 0 ? messageUnreadCount : undefined
                }
                className="mobile-header__message-badge"
              >
                <button
                  className="mobile-header__user-message-box"
                  onClick={handleMessageClick}
                  aria-label="Messages"
                >
                  <img
                    src={cdnAsset_BlyoKvvW}
                    alt=""
                    className="mobile-header__user-message-icon"
                    draggable={false}
                  />
                </button>
              </Badge>
            </div>
          </div>
        ) : (
          /* 未登录：全宽 auth-bar，从左到右：Logo | Login | 注册 CTA | 客服 | 搜索 */
          <div className="mobile-header__auth-bar">
            {/* Logo 靠左 */}
            <div className="mobile-header__logo">
              {logoSrc && (
                <img
                  src={logoSrc}
                  alt="Logo"
                  className="mobile-header__logo-img"
                  draggable={false}
                />
              )}
            </div>
            {/* 右侧按钮区域 */}
            <div className="mobile-header__actions">
              <button
                className="mobile-header__login-btn"
                onClick={handleLoginClick}
              >
                {t("auth.login")}
              </button>
              <div className="mobile-header__register-btn-wrap">
                <Button
                  variant="primary"
                  size="sm"
                  className="mobile-header__register-btn"
                  onClick={() =>
                    openLoginRegisterPopup({ defaultMode: "register" })
                  }
                >
                  {t("auth.register")}
                </Button>
                {/* 促销条：气泡 left 固定，图标组相对气泡起点定位 */}
                {homeRegisterPromoText ? (
                  <div className="mobile-header__register-promo">
                    <span className="mobile-header__promo-icon-wrap">
                      <span className="mobile-header__promo-icon-bg" />
                      <span className="mobile-header__promo-icon" />
                    </span>
                    <span className="mobile-header__register-promo-text">
                      {homeRegisterPromoText}
                    </span>
                  </div>
                ) : null}
              </div>
              <button
                className="mobile-header__service-icon"
                onClick={() => navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=1`)}
                aria-label="Customer Service"
              >
                <img
                  src={CustomerIconImg}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button>
              <button
                className="mobile-header__search-icon"
                onClick={() => navigate("/game-search")}
                aria-label="Search"
              >
                <img
                  src={GameSearchIconImg}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
